import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Schedule, Notes, Timer } from '@mui/icons-material';
import { meetingService } from '@services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const MeetingForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        topic: '',
        agenda: '',
        startTime: new Date(),
        duration: 60,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
    };

    const handleDateChange = (newDate) => {
        setFormData({
            ...formData,
            startTime: newDate,
        });
    };

    const validateForm = () => {
        if (!formData.topic.trim()) {
            setError('Meeting topic is required');
            return false;
        }
        if (formData.duration < 15) {
            setError('Meeting duration must be at least 15 minutes');
            return false;
        }
        if (new Date(formData.startTime) < new Date()) {
            setError('Meeting start time must be in the future');
            return false;
        }
        return true;
    };

    // Format date to yyyy-MM-ddTHH:mm:ss (LocalDateTime format - no timezone)
    const formatDateForBackend = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const formattedData = {
                topic: formData.topic.trim(),
                agenda: formData.agenda.trim(),
                startTime: formatDateForBackend(formData.startTime),
                duration: parseInt(formData.duration)
            };

            console.log('Sending formatted data:', formattedData);

            const response = await meetingService.createMeeting(formattedData);
            toast.success('Meeting created successfully!');
            navigate(`/interviews/${response.data.id}`);
        } catch (err) {
            console.error('Error creating meeting:', err);
            console.error('Error response:', err.response);
            const errorMessage = err.response?.data?.message || 'Failed to create meeting';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
                Create New Zoom Meeting
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Schedule a new meeting and share the link with participants
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <TextField
                    fullWidth
                    label="Meeting Topic"
                    value={formData.topic}
                    onChange={handleChange('topic')}
                    required
                    placeholder="e.g., Q1 Planning Meeting"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Schedule />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                <TextField
                    fullWidth
                    label="Agenda"
                    multiline
                    rows={4}
                    value={formData.agenda}
                    onChange={handleChange('agenda')}
                    placeholder="Describe the meeting agenda..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                <Notes />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label="Start Time"
                        value={formData.startTime}
                        onChange={handleDateChange}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: true,
                                sx: { mb: 3 }
                            }
                        }}
                        minDateTime={new Date()}
                    />
                </LocalizationProvider>

                <TextField
                    fullWidth
                    type="number"
                    label="Duration (minutes)"
                    value={formData.duration}
                    onChange={handleChange('duration')}
                    required
                    inputProps={{ min: 15, max: 480, step: 15 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Timer />
                            </InputAdornment>
                        ),
                    }}
                    helperText="Minimum 15 minutes, maximum 8 hours"
                    sx={{ mb: 4 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Schedule />}
                    >
                        {loading ? 'Creating...' : 'Create Meeting'}
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/interviews')}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default MeetingForm;